import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

/**
 * "Page ▢ of 20" — where the ▢ is the page you are on, and typing in it is how you
 * reach one the arrows would take a while to walk to.
 */
const clampPage = (value, totalPages) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return null;
  return Math.min(Math.max(parsed, 1), totalPages);
};

const PageIndicator = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  className = "",
  disabled = false,
}) => {
  const pageCount = Math.max(1, totalPages);
  const page = Math.min(Math.max(currentPage, 1), pageCount);

  // What the user has typed, until they commit it. Kept apart from `page` so that a
  // half-typed "1" on the way to "12" isn't a page load of its own.
  const [draft, setDraft] = useState(String(page));

  // Navigation from anywhere else — the arrows, a filter reset — shows through here.
  useEffect(() => {
    setDraft(String(page));
  }, [page]);

  const goTo = (value) => {
    const next = clampPage(value, pageCount);
    if (next === null) {
      setDraft(String(page)); // unreadable — put the current page back
      return;
    }
    setDraft(String(next));
    if (next !== page) onPageChange?.(next);
  };

  return (
    // mx-2 on top of the arrows' own margin, so the field reads as its own group
    // rather than crowding the arrow on either side of it.
    <span className="mx-2 inline-flex items-center gap-1 whitespace-nowrap">
      <span>Page</span>
      <input
        type="number"
        min={1}
        max={pageCount}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        // Committed on Enter or on leaving the field, never per keystroke — each
        // page change is a request.
        onKeyDown={(e) => {
          if (e.key === "Enter") goTo(e.currentTarget.value);
          if (e.key === "Escape") setDraft(String(page));
        }}
        onBlur={(e) => goTo(e.target.value)}
        aria-label={`Page, 1 to ${pageCount}`}
        title="Type a page and press Enter"
        // Narrow, with the number spinners hidden: they are what forced the box
        // wide, and stepping a page at a time is what the arrows are for.
        className={`w-8 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${className}`}
      />
      <span>of {pageCount}</span>
    </span>
  );
};

PageIndicator.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

export default PageIndicator;
